/**
 * The two DMD Financial Agency apps, shared by the App Launcher, the Sign In
 * page and the System Screens. Values come from the Claude Design "DMD App
 * System" page (DMD tokens / overrides table, Oct 2026).
 *
 * The folder and type names keep "Alliance" on purpose: these components are
 * ported from the alliance-* repos, and matching names keep fixes easy to
 * carry across.
 */

export type AllianceAppKey = 'dashboard' | 'contracting';

export interface AllianceApp {
  key: AllianceAppKey;
  /** Short name shown in the launcher, links rows and eyebrows */
  name: string;
  /** Eyebrow on the Sign In page and System Screens */
  fullName: string;
  url: string;
  /** One-line description shown in the App Launcher */
  description: string;
  /** App Bar background: Integrity navy for both DMD apps */
  bar: string;
  /** DMD gold 500. Fills: avatar, Next up, progress fill (4.5:1 on navy) */
  accent: string;
  /** Gold for text and rings on the bar: mono IDs, badge, ring (6.6:1) */
  accentOnBar: string;
  /** The normal (not staff, not impersonating) initials tile */
  avatar: { bg: string; fg: string };
  /** IDs and links in menus and on light surfaces (#4178ff fails for text) */
  menuAccent: string;
  /** Unread badge on the bar */
  badge: { bg: string; fg: string };
  /** Launcher / Sign In eyebrow tile. `bg` may be a CSS gradient */
  tile: { bg: string; icon: string };
  mode: 'light' | 'dark';
  /** background.default / background.paper */
  background: { default: string; paper: string };
  layout: 'sidebar' | 'top-nav';
}

/** Launcher and links-row order */
export const ALLIANCE_APP_ORDER: AllianceAppKey[] = [
  'dashboard',
  'contracting',
];

/** Gold gradient used for the Contracting tile and touch icon */
const DMD_GOLD_TILE =
  'linear-gradient(160deg, #d5ae63 0%, #b58a40 55%, #8f6827 100%)';

/**
 * App URLs from the design (confirmed Oct 15). Pages still hard-code the old
 * Amplify master URLs (master.d32mkyupzqy308 = Dashboard,
 * master.d31s5uxlnz93xr = Contracting); point them here as they're touched.
 */
export const ALLIANCE_APPS: Record<AllianceAppKey, AllianceApp> = {
  dashboard: {
    key: 'dashboard',
    name: 'Dashboard',
    fullName: 'DMD Dashboard',
    url: 'https://dmdagents.com',
    description: 'Commissions, production & team',
    bar: '#052a63',
    accent: '#a9905f',
    accentOnBar: '#c9b07a',
    avatar: { bg: '#a9905f', fg: '#031a3f' },
    menuAccent: '#2b5bd6',
    badge: { bg: '#c9b07a', fg: '#031a3f' },
    tile: { bg: '#052a63', icon: '#c9b07a' },
    mode: 'light',
    background: { default: '#f1f1f1', paper: '#ffffff' },
    layout: 'sidebar',
  },
  contracting: {
    key: 'contracting',
    name: 'Contracting',
    fullName: 'DMD Contracting',
    url: 'https://contracting.dmdagents.com',
    description: 'Agreements, licenses & carrier contracts',
    bar: '#052a63',
    accent: '#a9905f',
    accentOnBar: '#c9b07a',
    avatar: { bg: '#a9905f', fg: '#031a3f' },
    menuAccent: '#2b5bd6',
    badge: { bg: '#c9b07a', fg: '#031a3f' },
    tile: { bg: DMD_GOLD_TILE, icon: '#052a63' },
    mode: 'light',
    background: { default: '#f1f1f1', paper: '#ffffff' },
    layout: 'sidebar',
  },
};

/**
 * The collapsible Integrity group at the bottom of the App Launcher.
 * These open in a new tab.
 *
 * TODO(confirm): placeholder, approved for now. These reuse the Alliance
 * URLs until DMD's links are confirmed.
 */
export const INTEGRITY_LINKS = [
  {
    name: 'Register',
    description: 'Create your Integrity account',
    href: 'https://integrity.com/',
  },
  {
    name: 'Quotes & E-Apps',
    description: 'Run quotes and submit applications',
    href: 'https://integrity.com/',
  },
];

/**
 * The recruiting link shown in the user menu (RecruitLink). Unlike Alliance
 * (agent number in the query string), DMD links carry the upline's
 * registration GUID from Contracting's `/register/get-uuid`. Today the server
 * resolves it from the signed-in user's token; an impersonation-aware
 * version is coming (Oct 15).
 */
/**
 * Show RecruitLink while impersonating (admin or staff viewing as, an upline
 * viewing a downline). Off until Contracting's `/register/get-uuid` accepts
 * the impersonated agent's number (the parameter name is still to be
 * confirmed with the backend, Oct 15): today it resolves the GUID from the
 * signed-in user's token, which would be the wrong link. Staff for an agent
 * are fine today, because the API treats them as that agent.
 */
export const RECRUIT_LINK_WHEN_IMPERSONATING = false;

export const buildRecruitLink = (params: { uuid: string }) =>
  `${ALLIANCE_APPS.contracting.url}/sign-up/${encodeURIComponent(params.uuid)}`;

/**
 * Sidebar footer legal links and copyright.
 *
 * TODO(confirm): placeholder, approved for now. The labels and copyright are
 * the design's; the URLs are today's Integrity Terms/Privacy pages, since the
 * design has none.
 */
export const LEGAL_LINKS = [
  { label: 'Terms of use', href: 'https://integrity.com/terms-of-service/' },
  { label: 'Privacy policy', href: 'https://www.integrity.com/privacy-policy' },
];

export const copyrightText = () =>
  `© ${new Date().getFullYear()} DMD Financial Agency`;
